//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import { IScope } from 'angular';
import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { PrimaryRouteAnchor } from '@components/RouteAnchor';
import ActionButton from '@components/atoms/action-button/ActionButton';
import Indicator from '@components/atoms/indicator/Indicator';
import SidebarHeaderItem from '@components/atoms/sidebar-items/SidebarHeaderItem';
import SidebarLoadingPlaceholder from '@components/atoms/sidebar-items/SidebarLoadingPlaceholder';
import SidebarPageItemRouteless from '@components/atoms/sidebar-items/SidebarPageItemRouteless';
import Sidebar from '@components/organisms/sidebar/Sidebar';
import { getIsMediaLibraryStreamsEnabled } from '@media-library/redux/getMediaLibraryPage';
import makeAngularReactControllerWithScope, {
  applyScope,
} from '@react-bridge/angular-react-widget-with-scope';
import { IStream } from '@streams-drawer/model/IStream';
import {
  StreamsDrawerStreamType,
  StreamsDrawerTrackingIds,
  StreamsLaunchContext,
  getRecommendedStreamName,
  nameToStreamType,
} from '@streams-drawer/streams-drawer-constants';
import { alphabetizeLocaleCompare } from '@utils/arrayUtils';

const CREATE_STREAM_MOVED_MESSAGE = (
  <>
    <strong>Streams are moving to Content Library</strong>
    <p>
      You can now view all your stream content directly in Content Library.
      Going forward, new streams are created there instead of on this page.
      You can still edit or delete existing streams here.
    </p>
    <PrimaryRouteAnchor
      state='library.all-media'
      trackingId={
        StreamsDrawerTrackingIds.CREATE_STREAM_MOVED_CONTENT_LIBRARY_LINK
      }
    >
      Explore Streams in Content Library →
    </PrimaryRouteAnchor>
  </>
);

interface Props {
  connectPendingCount: number;
  hasRecommendedStreams: boolean;
  isModerationServices: boolean;
  isRecommendationsClient: boolean;
  isRefreshingStreams: boolean;
  launchContext: StreamsLaunchContext;
  noSelectedStream: boolean;
  onOpenNewStreamModal: () => void;
  onStreamClick: ({ stream }: { stream: IStream }) => void;
  onSummaryClick: () => void;
  recommendationStreams: IStream[];
  selectedStream: IStream;
  shouldNotAllowUgcStreams: boolean;
  showRecommendedSummary: boolean;
  streams: IStream[];
  taggedByCuralatePendingCount: number;
}

export const StreamsDrawerSidebarReact = ({
  connectPendingCount,
  hasRecommendedStreams,
  isModerationServices,
  isRecommendationsClient,
  isRefreshingStreams,
  launchContext,
  noSelectedStream,
  onOpenNewStreamModal,
  onStreamClick,
  onSummaryClick,
  recommendationStreams,
  selectedStream,
  shouldNotAllowUgcStreams,
  showRecommendedSummary,
  streams,
  taggedByCuralatePendingCount,
}: Props) => {
  // TODO: PD-319070 - Remove streams/collections branching once media_library_streams is released
  const isStreamsExperienceEnabled = useSelector(
    getIsMediaLibraryStreamsEnabled,
  );
  const isCreateStreamDisabled =
    isStreamsExperienceEnabled &&
    launchContext === StreamsLaunchContext.GALLERIES;

  const hasAlreadyCreatedStream =
    streams.filter(
      (stream) =>
        nameToStreamType(stream) !== StreamsDrawerStreamType.GALLERY_UPLOADS,
    ).length > 0;
  const isUgcOnlyClientAndStreamAlreadyCreated =
    shouldNotAllowUgcStreams && hasAlreadyCreatedStream;
  const shouldShowCreateStreamButton =
    !isModerationServices && !isUgcOnlyClientAndStreamAlreadyCreated;

  const pendingItemsCount = (stream: IStream) => {
    const isTaggedByCuralateStream =
      nameToStreamType(stream) === StreamsDrawerStreamType.TAGGED_BY_CURALTE;
    const isConnectStream =
      nameToStreamType(stream) === StreamsDrawerStreamType.CONNECT;
    if (isTaggedByCuralateStream) {
      return taggedByCuralatePendingCount;
    } else if (isConnectStream) {
      return connectPendingCount;
    } else {
      return null;
    }
  };

  const sidebarContent = (
    <>
      {isRecommendationsClient && (
        <>
          <SidebarHeaderItem text='Recommended Streams' />

          {showRecommendedSummary && (
            <SidebarPageItemRouteless
              isActive={noSelectedStream}
              onClick={onSummaryClick}
              text='All Recommendations'
            />
          )}

          {recommendationStreams.map((stream) => (
            <SidebarPageItemRouteless
              isActive={selectedStream?.id === stream.id}
              key={stream.id}
              linkBadge={
                <Indicator
                  showIf={(pendingItemsCount(stream) ?? 0) > 0}
                  title='You have unmoderated items'
                />
              }
              onClick={() => onStreamClick({ stream })}
              text={getRecommendedStreamName(stream)}
            />
          ))}
        </>
      )}

      <SidebarHeaderItem
        headerAction={
          shouldShowCreateStreamButton ? (
            <ActionButton
              disabledPopoverContent={CREATE_STREAM_MOVED_MESSAGE}
              iconName='add'
              isDisabled={isCreateStreamDisabled}
              label='New Content Stream'
              onClick={onOpenNewStreamModal}
              trackingId={StreamsDrawerTrackingIds.NEW_CONTENT_STREAM_BUTTON}
            />
          ) : (
            <></>
          )
        }
        text={`${hasRecommendedStreams ? 'Your' : ''} Media Streams`}
      />

      {alphabetizeLocaleCompare(streams, (stream) => stream.name).map(
        (stream) => (
          <SidebarPageItemRouteless
            isActive={selectedStream?.id === stream.id}
            key={stream.id}
            onClick={() => onStreamClick({ stream })}
            text={stream.name}
          />
        ),
      )}
    </>
  );

  return (
    <Sidebar>
      {isRefreshingStreams ? <LoadingPlaceholder /> : sidebarContent}
    </Sidebar>
  );
};

const LoadingPlaceholder = styled(SidebarLoadingPlaceholder)`
  margin-top: 50px;
`;

angular.module('curalate').component('streamsDrawerSidebarReact', {
  bindings: {
    connectPendingCount: '<',
    hasRecommendedStreams: '<',
    isModerationServices: '<',
    isRecommendationsClient: '<',
    isRefreshingStreams: '<',
    launchContext: '<',
    noSelectedStream: '<',
    onOpenNewStreamModal: '&',
    onStreamClick: '&',
    onSummaryClick: '&',
    recommendationStreams: '<',
    selectedStream: '<',
    shouldNotAllowUgcStreams: '<',
    showRecommendedSummary: '<',
    streams: '<',
    taggedByCuralatePendingCount: '<',
  },
  controller: makeAngularReactControllerWithScope(
    (scope: { $ctrl: Props } & IScope) => {
      return (
        <StreamsDrawerSidebarReact
          connectPendingCount={scope.$ctrl.connectPendingCount}
          hasRecommendedStreams={scope.$ctrl.hasRecommendedStreams}
          isModerationServices={scope.$ctrl.isModerationServices}
          isRecommendationsClient={scope.$ctrl.isRecommendationsClient}
          isRefreshingStreams={scope.$ctrl.isRefreshingStreams}
          launchContext={scope.$ctrl.launchContext}
          noSelectedStream={scope.$ctrl.noSelectedStream}
          onOpenNewStreamModal={applyScope(
            scope,
            scope.$ctrl.onOpenNewStreamModal,
          )}
          onStreamClick={applyScope(scope, scope.$ctrl.onStreamClick)}
          onSummaryClick={applyScope(scope, scope.$ctrl.onSummaryClick)}
          recommendationStreams={scope.$ctrl.recommendationStreams}
          selectedStream={scope.$ctrl.selectedStream}
          shouldNotAllowUgcStreams={scope.$ctrl.shouldNotAllowUgcStreams}
          showRecommendedSummary={scope.$ctrl.showRecommendedSummary}
          streams={scope.$ctrl.streams}
          taggedByCuralatePendingCount={
            scope.$ctrl.taggedByCuralatePendingCount
          }
        />
      );
    },
  ),
});
